import { memo, useEffect, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { Skeleton } from '@rocco/components/skeleton';
import { getUserOccupation } from '~/modules/user';
import type { ContactSuggestion } from '../../types';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';

/**
 * A list of new message contact suggestions in the new message chat box.
 * @see {@link ChatBox}
 */
export function ChatBoxSuggestionsList({
    className,
    suggestions,
    isLoading = false,
    hasMore = false,
    onSuggestionClick,
    onLoadMore,
}: {
    className?: string;
    suggestions: ContactSuggestion[];
    isLoading?: boolean;
    hasMore?: boolean;
    onSuggestionClick: (suggestion: ContactSuggestion) => void;
    onLoadMore?: () => void;
}) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

    // Trigger load more suggestions when the user scrolls to the bottom.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = scrollContainerRef.current;
        if (!sentinel || !scrollContainer || !hasMore || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry?.isIntersecting &&
                    onLoadMore &&
                    hasMore &&
                    !isLoading
                ) {
                    onLoadMore();
                }
            },
            { root: scrollContainer }
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoading, onLoadMore]);

    return (
        <section className={cn('flex flex-col gap-[8px]', className)}>
            <h3 className="text-[12px] leading-tight text-gray-600">
                Suggestions
            </h3>
            <div
                ref={scrollContainerRef}
                className="min-h-0 flex-1 overflow-y-auto"
            >
                <ul
                    className="relative flex flex-col divide-y divide-white"
                    aria-label="Contact suggestions"
                    aria-live="polite"
                    aria-busy={isLoading}
                >
                    {suggestions.length ? (
                        <>
                            {suggestions.map((suggestion) => (
                                <li
                                    key={suggestion.ID}
                                    className="flex items-center gap-[8px] p-[8px] hover:bg-white/50 focus-visible:bg-white/50"
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Add to recipients"
                                    onClick={() =>
                                        onSuggestionClick(suggestion)
                                    }
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            e.preventDefault();
                                            onSuggestionClick(suggestion);
                                        }
                                    }}
                                >
                                    <ParticipantAvatar
                                        className="size-[36px]"
                                        fallbackClassName="!text-[14px]"
                                        participant={suggestion}
                                    />
                                    <div className="min-w-0 flex-1 space-y-[2px]">
                                        <div className="truncate text-[14px] font-medium leading-tight">
                                            {suggestion.Name}
                                        </div>
                                        <div className="truncate text-[12px] leading-tight opacity-70">
                                            {getUserOccupation(suggestion)}
                                        </div>
                                    </div>
                                </li>
                            ))}
                            {hasMore && (
                                <div
                                    ref={loadMoreSentinelRef}
                                    className="invisible absolute inset-x-0 bottom-[500px] h-px"
                                    aria-hidden="true"
                                ></div>
                            )}
                        </>
                    ) : isLoading ? (
                        <SuggestionsListSkeleton />
                    ) : (
                        <li className="p-[8px] text-center text-[16px] leading-tight text-gray-600">
                            No suggestions found
                        </li>
                    )}
                </ul>
            </div>
        </section>
    );
}

const SuggestionsListSkeleton = memo(function SuggestionsListSkeleton() {
    return (
        <>
            {Array.from({ length: 8 }).map((_, index) => (
                <li key={index} className="flex items-center gap-[8px] p-[8px]">
                    <Skeleton className="size-[36px] rounded-full bg-gray-500" />
                    <div className="flex-1 space-y-[4px]">
                        <Skeleton className="h-[14px] w-1/2 bg-gray-500" />
                        <Skeleton className="h-[10px] w-1/6 bg-gray-500" />
                    </div>
                </li>
            ))}
        </>
    );
});
